import { HEATMAP_FOLDER_ITEM_ID } from '@const/dashboard.constants';
import { HEALTH_STATUS_ITEM_ID, HEATMAP_ITEM_ID } from '@const/healthStatus.constants';
import { AdminFeatures } from '@dto/groupsResponse.dto';
import { IMenuItem, MenuItem } from '@dto/menuResponse.dto';
import { DataNode } from 'antd/es/tree';
import { ReactNode, type JSX } from 'react';
import { ClassType } from 'src/data/Class';
import { GLOB } from 'src/util/Glob';

// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
export interface MenuNode extends DataNode, IMenuItem {
  path?: string;
  keyClass?: ClassType;
  parent?: MenuNode;
  children?: MenuNode[];
  component?: JSX.Element;
  divider?: boolean;
  titleText?: string;
  title?: ReactNode;
  selectable?: boolean;
  group_id?: number;


  // ============== filters

  notInDemo?: boolean;
  notInProduction?: boolean;
  notOnDocker?: boolean;
  hwType?: string | string[];
  classType?: ClassType;
  notActive?: boolean;
  adminOnly?: boolean;
  managerOnly?: boolean;
  adminFeature?: keyof AdminFeatures;
  blockReadonly?: boolean;
  treeOnly?: boolean;

  isSearchTree?: boolean;

  isGroupFolder?: boolean;
  isGroupedGroupFolder?: boolean;
  isPersonalGroupOrFolder?: boolean;
  isStorageGroup?: boolean;
}

const DIVIDER_ITEM_ID = 'divider';

// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
export abstract class MenuNode {

  static readonly DASH_EXCLUDED_ITEM_IDS = [HEALTH_STATUS_ITEM_ID, HEATMAP_FOLDER_ITEM_ID, HEATMAP_ITEM_ID];
  static readonly DASH_EXCLUDED_ITEM_IDS_ALL = [...MenuNode.DASH_EXCLUDED_ITEM_IDS, DIVIDER_ITEM_ID];
  static readonly GROUPS_DIVIDER_KEY = 'groups-divider';

  static isDashExcluded(node: MenuNode) {
    if (this.DASH_EXCLUDED_ITEM_IDS_ALL.includes(node.itemId)) return true;
    if (node.parent) {
      return MenuNode.isDashExcluded(node.parent) as boolean;
    }
    return false;
  }

  static getDivider(key: string, treeOnly: boolean): MenuNode {
    return {
      key,
      itemId: DIVIDER_ITEM_ID,
      title: <hr />,
      className: 'xm-divider',
      selectable: false,
      adminOnly: true,
      divider: true,
      isLeaf: true,
      treeOnly
    };
  }

  static getGroupId(item: MenuNode): number {
    if (!item) return;
    if (item.group_id) return item.group_id;
    return MenuNode.getGroupId(item.parent);
  }

  static getGroupPathSegment(group: MenuNode) {
    return group.itemId;
  }

  static getNodePath(node: MenuNode) {
    return node.path ? node.path : GLOB.getItemPath(node);
  }

  /**
   * Get array of parent node keys including self
   * @param node
   * @returns
   */
  static getParentKeys(node?: MenuNode): string[] {
    if (!node?.parent) return [];
    const parents = MenuNode.getParentKeys(node.parent);
    parents.push(node.key as string);
    return parents;
  }

  /**
   * Get array of parent nodes from top to self
   * @param node
   * @returns
   */
  static getParentNodes(node: MenuNode): MenuNode[] {
    if (!node) return [];
    const arr = MenuNode.getParentNodes(node.parent);
    arr.push(node);
    return arr;
  }

  /**
   * Gets top parent in tree or stops at condition
   * @param node node which parent to get
   * @param condition optional condition to stop sooner
   * @returns node
   */
  static getTopParent(node: MenuNode, condition?: (node: MenuNode) => boolean): MenuNode {
    if (!node?.parent) return;
    if (condition?.(node)) return node;
    const parent = MenuNode.getTopParent(node.parent, condition);
    if (!parent) return node;
    return parent;
  }

  static getFirstNodeParentNoLeaf(node: MenuNode): MenuNode {
    if (!node?.parent) return;
    if (!node.parent.isLeaf) return node.parent;
    return MenuNode.getFirstNodeParentNoLeaf(node.parent);
  }

  /** Sets parent references on all nodes of the subtree. */
  static mapParent(treeNodes: MenuNode[], parent: MenuNode) {
    if (!treeNodes) return;
    for (const tn of treeNodes) {
      //if (!tn.parent)
      tn.parent = parent;
      MenuNode.mapParent(tn.children, tn);
    }
  }

  /** Gets the closest ancestor (or the node itself) carrying a hwType, if any. */
  static getHwTypeParent(node: MenuNode): MenuNode {
    const result = MenuNode.getHwTypeParentRekur(node);
    if (result?.hwType) return result;
  }

  private static getHwTypeParentRekur(node: MenuNode): MenuNode {
    if (!node) return;
    const parent = MenuNode.getHwTypeParent(node.parent);
    if (parent?.hwType) return parent;
    return node;
  }

  static findDeepNode(nodes: MenuNode[], item: string, hwType?: string, group?: string, ignoreGroupId = false): MenuNode {
    if (!nodes) return;
    const strip = (value?: string) => ignoreGroupId && value ? value.split('&group_id=')[0] : value;
    for (const node of nodes) {
      if (group && !node.group_id && group !== MenuNode.getGroupPathSegment(node) && !node.key.toString().startsWith(group + '/') && !node.isGroupFolder) continue;
      const nodeItemId = typeof node.itemId === 'string' ? strip(node.itemId) : node.itemId;
      if (nodeItemId === item || '/' + nodeItemId === item) return node;
      if (node.pageType === item && (!hwType || !node.url || node.url.includes(hwType))) return node;
      if (strip(node.path)?.endsWith(item)) return node;//check for groups
      if (strip(node.url)?.endsWith(item)) return node;//check for groups
      if (node.children) {
        const result = MenuNode.findDeepNode(node.children, item, hwType, group, ignoreGroupId);
        if (result) {
          return result;
        }
      }
    }
  }

  /**
   * Create MenuNode from MenuItem
   * @param item
   * @param keyPrefix
   * @returns
   */
  static menuItemToNode(item: MenuItem, keyPrefix = ''): MenuNode {
    return {
      ...item,
      key: keyPrefix + item.url,// (item.itemId && typeof item.itemId === 'string' ? item.itemId : item.url),
      titleText: item.title,
      isLeaf: !!item.isLeaf,
      children: item.children?.map(mi => MenuNode.menuItemToNode(mi)),
      ...(item.itemId?.startsWith(DIVIDER_ITEM_ID) ? {
        title: <hr />,
        className: 'xm-divider',
        selectable: false,
        adminOnly: true,
        divider: true,
        isLeaf: true,
        treeOnly: false,
        titleText: undefined
      } : {}),
      //path: GLOB.getItemPath(item.url)
    };
  }

  /**
   * Gets item to select in menu on folder expand
   * @param children
   * @returns
   */
  static selectFirst(children: MenuNode[]) {
    let selected = children.find(mn => mn.isDefault);
    if (!selected) selected = children.find(mn => mn.isLeaf && mn.itemId !== DIVIDER_ITEM_ID);
    if (!selected) selected = children.find(mn => mn.itemId !== DIVIDER_ITEM_ID);
    if (!selected) selected = children[0];
    return selected;
  }

}